import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';

import { useClarity, useCollapsedNav, useInstitution, useSeller, useUser } from 'hooks';
import { ErrorBoundary } from 'utils';

// Containers
import CommunicationAlerts from 'containers/common/CommunicationAlerts';
import CommunicationBanners from 'containers/common/CommunicationBanners';
import CommunicationPopups from 'containers/common/CommunicationPopups';
import EskolarePolicyAcceptancePopup from 'containers/common/EskolarePolicyAcceptancePopup';
import SpotlightProvider from 'containers/common/Spotlight';
import ChangeContextMenu from 'containers/widgets/ChangeContextMenu';
import CommonHelpCenter from 'containers/widgets/CommonHelpCenter';
import CommonJobNotification from 'containers/widgets/CommonJobNotification';

// Components
import { Mui } from 'components';
import { Footer, PageBannerProvider, TopBar } from 'components/layouts';
import { DRAWER_WIDTH, RAIL_WIDTH } from 'components/layouts/NavBar/constants';
import { TOP_BAR_HEIGHT } from 'components/layouts/TopBar/constants';

// Local components
import AccountNavBar from './AccountNavBar';
import InstitutionNavBar from './InstitutionNavBar';
import SellerNavBar from './SellerNavBar';

const useStyles = makeStyles(theme => ({
  globalWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    '@media print': {
      display: 'block',
      height: 'auto',
    },
  },
  globalContent: {
    flex: 1,
    transform: 'translateZ(0)',
    overflow: 'hidden',
    '@media print': {
      overflow: 'visible',
      transform: 'none',
    },
  },
  root: {
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
    '@media print': {
      display: 'block',
      height: 'auto',
      overflow: 'visible',
    },
  },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    paddingTop: TOP_BAR_HEIGHT,
    maxWidth: '100%',
    transition: theme.transitions.create('padding-left', {
      duration: theme.transitions.duration.short,
    }),
    [theme.breakpoints.up('md')]: {
      paddingLeft: props => (props.isNavCollapsed ? RAIL_WIDTH : DRAWER_WIDTH),
    },
    '@media print': {
      overflow: 'visible',
      paddingTop: 0,
      paddingLeft: 0,
    },
  },
  contentContainer: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    maxWidth: '100%',
    '@media print': {
      overflow: 'visible',
    },
  },
  content: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflow: 'auto',
    maxWidth: '100%',
    '@media print': {
      display: 'block',
      minHeight: 0,
      overflow: 'visible',
    },
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    '@media print': { display: 'block' },
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
  },
  contentMain: {
    flex: '1 1 auto',
    minWidth: 0,
  },
}));

// Renders nothing; exists so useClarity is only initialized on panel contexts
// (institutions/sellers), matching the previous per-layout behavior.
function ClarityLoader() {
  useClarity();
  return null;
}

/**
 * Shared layout for the three logged-in contexts: /institutions/:institutionSlug,
 * /sellers/:sellerSlug and /accounts. Rendered by a single route (path array), so
 * the chrome (TopBar, wrappers) persists across context switches instead of
 * remounting.
 */
function AppLayout({ children }) {
  // States
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [isNavCollapsed, setNavCollapsed] = useCollapsedNav();

  // Hooks
  const classes = useStyles({ isNavCollapsed });
  const { sellerSlug, institutionSlug } = useParams();
  const { institution } = useInstitution();
  const { seller, sellerPermissions, schoolManagement } = useSeller();
  const { user } = useUser();

  // Constants
  let context = 'accounts';
  if (sellerSlug) context = 'sellers';
  else if (institutionSlug) context = 'institutions';

  const isPanel = context !== 'accounts';
  const data = context === 'sellers' ? seller : institution;

  let homeTo = '/accounts';
  if (context === 'sellers') homeTo = `/sellers/${sellerSlug}`;
  else if (context === 'institutions') homeTo = `/institutions/${institutionSlug}`;

  const pageBanner = useMemo(
    () => ({
      renderBanner: props =>
        isPanel && data?.slug ? (
          <CommunicationBanners type={context} slug={data.slug} {...props} />
        ) : null,
    }),
    [isPanel, data?.slug, context],
  );

  let navBar = (
    <AccountNavBar
      onMobileClose={() => setMobileNavOpen(false)}
      openMobile={isMobileNavOpen}
      isNavCollapsed={isNavCollapsed}
      onToggleCollapse={() => setNavCollapsed(!isNavCollapsed)}
    />
  );
  if (context === 'sellers')
    navBar = (
      <SellerNavBar
        schoolManagement={schoolManagement}
        onMobileClose={() => setMobileNavOpen(false)}
        openMobile={isMobileNavOpen}
        sellerPermissions={sellerPermissions}
        isNavCollapsed={isNavCollapsed}
        onToggleCollapse={() => setNavCollapsed(!isNavCollapsed)}
      />
    );
  else if (context === 'institutions')
    navBar = (
      <InstitutionNavBar
        onMobileClose={() => setMobileNavOpen(false)}
        openMobile={isMobileNavOpen}
        isNavCollapsed={isNavCollapsed}
        onToggleCollapse={() => setNavCollapsed(!isNavCollapsed)}
      />
    );

  return (
    <SpotlightProvider>
      <div className={classes.globalWrapper}>
        {isPanel && data?.slug && (
          <CommunicationAlerts key={`alerts-${context}`} type={context} slug={data.slug} />
        )}
        {isPanel && data?.slug && (
          <CommunicationPopups key={`popups-${context}`} type={context} slug={data.slug} />
        )}
        <div className={classes.globalContent}>
          <div className={classes.root}>
            <TopBar
              context={context}
              homeTo={homeTo}
              data={data}
              user={user}
              onMobileNavOpen={() => setMobileNavOpen(true)}>
              {isPanel && data?.slug && (
                <CommonHelpCenter
                  context={context}
                  slug={data.slug}
                  customerSuccesses={data.customerSuccesses}
                />
              )}
              {isPanel && <CommonJobNotification />}
              <ChangeContextMenu />
            </TopBar>
            <Mui.Box displayPrint="none">{navBar}</Mui.Box>
            <div className={classes.wrapper}>
              <div className={classes.contentContainer}>
                <div className={classes.content} id="content">
                  <div className={classes.contentRow}>
                    <div className={classes.contentMain}>
                      <PageBannerProvider value={pageBanner}>
                        <ErrorBoundary>{children}</ErrorBoundary>
                      </PageBannerProvider>
                    </div>
                    <div id="page-feed-portal" style={{ display: 'contents' }} />
                  </div>
                  <Mui.Box mt="auto" pt={3} flexShrink={0} displayPrint="none">
                    <Footer />
                  </Mui.Box>
                </div>
              </div>
            </div>
            {context === 'sellers' && <EskolarePolicyAcceptancePopup />}
            {isPanel && <ClarityLoader />}
          </div>
        </div>
      </div>
    </SpotlightProvider>
  );
}

AppLayout.propTypes = {
  children: PropTypes.any,
};

export default AppLayout;
